import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { t } from '../lib/i18n.ts';
import { Button, Card, ErrorText, Field, Input } from '../lib/ui.tsx';
import { SolfoTile } from '../components/Logo.tsx';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ token: string; user: { role: string } }>('/api/auth/login', {
        body: { email, password },
      });
      await login(res.token);
      navigate(res.user.role === 'coach' ? '/coach' : '/app');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden p-4">
      {/* floating notes, purely decorative */}
      <div aria-hidden className="pointer-events-none absolute inset-0 select-none">
        <span className="absolute top-[12%] left-[8%] text-4xl opacity-10">♪</span>
        <span className="absolute top-[28%] right-[10%] text-6xl opacity-10">♫</span>
        <span className="absolute bottom-[18%] left-[16%] text-5xl opacity-10">♬</span>
        <span className="absolute right-[20%] bottom-[10%] text-3xl opacity-10">♪</span>
      </div>
      <div className="animate-fade-up w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="rounded-3xl shadow-lg shadow-brand-600/25">
            <SolfoTile size={64} />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">solfo</h1>
          <p className="-mt-2 text-sm text-slate-500">{t('login.tagline', 'practice that sings back')}</p>
        </div>
        <Card>
          <form onSubmit={onSubmit} className="space-y-4">
            <h2 className="text-lg font-semibold">{t('login.title', 'Log in')}</h2>
            <Field label={t('login.email', 'Email')}>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </Field>
            <Field label={t('login.password', 'Password')}>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Field>
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? '…' : t('login.title', 'Log in')}
            </Button>
            <ErrorText error={error} />
          </form>
        </Card>
        <p className="mt-4 text-center text-sm text-slate-500">
          Vocal coach? <Link to="/register" className="font-medium text-brand-700">Create a studio account</Link>
        </p>
        <p className="mt-1 text-center text-xs text-slate-400">
          Students join through their coach's invite link — there is no public student sign-up.
        </p>
      </div>
    </div>
  );
}
